import { cn } from '@/lib/utils';
import type { GroupPosition } from '../utils/types';

interface GroupedCalloutProps {
  id: string;
  content: React.ReactNode;
  groupPosition: GroupPosition;
  borderColor?: string;
  faded?: boolean;
  className?: string;
  onClick?: () => void;
}

/**
 * Callout component that groups sequential items visually
 * Used for Remarks and SSRs
 */
export function GroupedCallout({
  id,
  content,
  groupPosition,
  borderColor = 'border-slate-400',
  faded = false,
  className,
  onClick,
}: GroupedCalloutProps) {
  const positionClasses = {
    single: 'rounded-lg mb-2 md:mb-3',
    first: 'rounded-t-lg border-b-0',
    middle: 'border-t-0 border-b-0',
    last: 'rounded-b-lg border-t-0 mb-2 md:mb-3',
  };

  return (
    <div
      onClick={onClick}
      className={cn(
        'bg-white border border-slate-200 px-3 md:px-4 py-2.5 md:py-3',
        'border-l-4',
        borderColor,
        positionClasses[groupPosition],
        faded && 'opacity-40',
        onClick && 'cursor-pointer hover:bg-slate-50 transition-colors',
        className
      )}
    >
      <div className="flex items-start gap-2 md:gap-3 font-mono text-xs md:text-sm">
        <span className="text-slate-400 font-medium shrink-0 w-10 md:w-12">{id}</span>
        <div className="flex-1 text-slate-700 font-medium break-words">
          {content}
        </div>
      </div>
    </div>
  );
}

interface CalloutListProps<T extends { id: string }> {
  items: T[];
  renderContent: (item: T) => React.ReactNode;
  getBorderColor?: (item: T) => string;
  isFaded?: (item: T) => boolean;
  onItemClick?: (item: T) => void;
  className?: string;
}

/**
 * List of grouped callouts with automatic grouping
 */
export function CalloutList<T extends { id: string }>({
  items,
  renderContent,
  getBorderColor,
  isFaded,
  onItemClick,
  className,
}: CalloutListProps<T>) {
  const getOTNumber = (id: string): number | null => {
    const match = id.match(/OT(\d+)/);
    return match ? parseInt(match[1]) : null;
  };

  const getGroupPosition = (index: number): GroupPosition => {
    const currentNum = getOTNumber(items[index].id);
    const prevNum = index > 0 ? getOTNumber(items[index - 1].id) : null;
    const nextNum = index < items.length - 1 ? getOTNumber(items[index + 1].id) : null;

    if (currentNum === null) return 'single';

    const isPrevSequential = prevNum !== null && currentNum === prevNum + 1;
    const isNextSequential = nextNum !== null && currentNum === nextNum - 1;

    if (isPrevSequential && isNextSequential) return 'middle';
    if (isPrevSequential) return 'last';
    if (isNextSequential) return 'first';
    return 'single';
  };

  return (
    <div className={className}>
      {items.map((item, index) => (
        <GroupedCallout
          key={item.id}
          id={item.id}
          content={renderContent(item)}
          groupPosition={getGroupPosition(index)}
          borderColor={getBorderColor?.(item)}
          faded={isFaded?.(item)}
          onClick={onItemClick ? () => onItemClick(item) : undefined}
        />
      ))}
    </div>
  );
}
